import { createStore } from 'zustand/vanilla'

export interface ScopeDraft {
  drafts: Record<string, number>
  failed: Record<string, string>
  acknowledged: Record<string, number>
}
export const EMPTY_SCOPE: ScopeDraft = { drafts: {}, failed: {}, acknowledged: {} }
interface DraftState {
  scopes: Record<string, ScopeDraft>
  saving: boolean
  setGrade: (scope: string, key: string, current: number | null, value: number | null) => void
  beginSave: () => boolean
  endSave: () => void
  saved: (scope: string, key: string, sentValue: number) => void
  failed: (scope: string, key: string, message: string) => void
  clearAcknowledged: (scope: string) => void
  reset: () => void
}

/** Memory only. Owned by the mobile page, so unmounting an item never discards edits. */
export function createHandheldDraftStore() {
  return createStore<DraftState>((set, get) => ({
    scopes: {},
    saving: false,
    setGrade: (scope, key, current, value) => {
      if (get().saving) return
      const previous = get().scopes[scope] ?? EMPTY_SCOPE
      const drafts = { ...previous.drafts }
      const failed = { ...previous.failed }
      if (value == null || current === value || drafts[key] === value) delete drafts[key]
      else drafts[key] = value
      delete failed[key]
      set({ scopes: { ...get().scopes, [scope]: { ...previous, drafts, failed } } })
    },
    beginSave: () => {
      if (get().saving) return false
      set({ saving: true })
      return true
    },
    endSave: () => set({ saving: false }),
    saved: (scope, key, sentValue) => {
      const previous = get().scopes[scope] ?? EMPTY_SCOPE
      const drafts = { ...previous.drafts }
      const failed = { ...previous.failed }
      if (drafts[key] === sentValue) delete drafts[key]
      delete failed[key]
      set({ scopes: { ...get().scopes, [scope]: { drafts, failed, acknowledged: { ...previous.acknowledged, [key]: sentValue } } } })
    },
    failed: (scope, key, message) => {
      const previous = get().scopes[scope] ?? EMPTY_SCOPE
      set({ scopes: { ...get().scopes, [scope]: { ...previous, failed: { ...previous.failed, [key]: message } } } })
    },
    clearAcknowledged: (scope) => {
      const previous = get().scopes[scope]
      if (!previous || Object.keys(previous.acknowledged).length === 0) return
      set({ scopes: { ...get().scopes, [scope]: { ...previous, acknowledged: {} } } })
    },
    reset: () => set({ scopes: {}, saving: false }),
  }))
}
export type HandheldDraftStore = ReturnType<typeof createHandheldDraftStore>
export function hasHandheldDrafts(store: HandheldDraftStore): boolean {
  return Object.values(store.getState().scopes).some(scope => Object.keys(scope.drafts).length > 0)
}
